import Axios from "axios";

export interface ProviderCommunicationThread {
    id: number;
    patientId: number;
    prescriberNPI: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId: number;
    isResolved: boolean;
    resolvedDate?: string | null;
    resolvedBy?: string | null;
    createdDate: string;
    lastMessageDate: string;
    lastMessageBody?: string | null;
    lastMessageInbound?: boolean | null;
    lastMessageAuthorUserName?: string | null;
    unreadCount: number;
    patientFirstName?: string | null;
    patientLastName?: string | null;
    patientDOB?: string | null;
}

export interface ProviderCommunicationMessage {
    id: number;
    threadId: number;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    inbound: boolean;
    body: string;
    authorUserName: string;
    authorType: number;
    createdDate: string;
}

export interface ThreadDetailResponse {
    thread: ProviderCommunicationThread;
    messages: ProviderCommunicationMessage[];
}

export interface InitiateRequest {
    patientId: number;
    prescriberNPI: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId?: number | null;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    body: string;
}

export interface ReplyRequest {
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    body: string;
}

export interface PracticeOption {
    id: number;
    name?: string | null;
    isActive?: boolean;
}

const BASE_URL = "/api/ProviderCommunications";

export const providerCommunicationsService = {
    async listThreads(patientId?: number, includeResolved = false): Promise<ProviderCommunicationThread[]> {
        const response = await Axios.get<ProviderCommunicationThread[]>(`${BASE_URL}/threads`, {
            params: {
                patientId: patientId ?? undefined,
                includeResolved,
            },
        });
        return response.data;
    },

    async getThread(id: number): Promise<ThreadDetailResponse> {
        const response = await Axios.get<ThreadDetailResponse>(`${BASE_URL}/threads/${id}`);
        return response.data;
    },

    async getPracticesForNpi(npi: string): Promise<PracticeOption[]> {
        const response = await Axios.get<PracticeOption[]>(`${BASE_URL}/practices-for-npi`, {
            params: { npi },
        });
        return response.data;
    },

    async initiate(request: InitiateRequest): Promise<ThreadDetailResponse> {
        const response = await Axios.post<ThreadDetailResponse>(`${BASE_URL}/threads/initiate`, request);
        return response.data;
    },

    async reply(threadId: number, request: ReplyRequest): Promise<ThreadDetailResponse> {
        const response = await Axios.post<ThreadDetailResponse>(`${BASE_URL}/threads/${threadId}/messages`, request);
        return response.data;
    },

    async setResolved(threadId: number, isResolved: boolean): Promise<ProviderCommunicationThread> {
        const response = await Axios.post<ProviderCommunicationThread>(
            `${BASE_URL}/threads/${threadId}/resolve`,
            { isResolved }
        );
        return response.data;
    },

    async markRead(threadId: number): Promise<void> {
        await Axios.post(`${BASE_URL}/threads/${threadId}/mark-read`);
    },

    async getUnreadCount(): Promise<number> {
        const response = await Axios.get<{ count: number }>(`${BASE_URL}/unread-count`);
        return response.data.count;
    },
};

export const providerCommunicationsPermissions = {
    view: "Provider Communications/View",
    send: "Provider Communications/Send",
};
